import { isApiError } from "@simpro/api-client";
import { cn, focusRing } from "@simpro/ui";
import { useQuery } from "@tanstack/react-query";
import { Phone, RotateCw } from "lucide-react";
import { isRouteErrorResponse, useRouteError } from "react-router";
import { salonProfileQuery } from "@/features/salon";
import { formatPhone } from "@/lib/formatPhone";
import { SalonMark } from "@/shared/layout/SalonMark";
import { pill } from "@/shared/styles/pill";
import { NotFoundPage } from "./NotFoundPage";

/**
 * A route that failed to load or render (design note S2, round 2 C28). A missing page is the 404.
 * Anything else is a card with a ringed ↻, never red, Reload, and the salon's phone as the fallback.
 * Without the header, since its links may be what failed: only the salon's mark and name, which the
 * device already has. The thrown message is a developer's words, so it is not shown.
 *
 * Restyled under design note salon-booking-premium-redesign.md section 5.3 item 16: a plain hairline
 * card in place of the stack, an eyebrow over the title in the display face, Reload the one accent and
 * the call an ink outline.
 */
export function RouteErrorPage() {
  const error = useRouteError();
  const { data: salon } = useQuery(salonProfileQuery());

  if (isRouteErrorResponse(error) && error.status === 404) return <NotFoundPage />;
  if (isApiError(error) && error.isNotFound) return <NotFoundPage />;

  return (
    <div className="flex min-h-screen flex-col bg-base">
      {salon ? (
        <header className="mx-auto flex w-full max-w-[77.5rem] items-center gap-3 border-b border-edge-subtle px-4 py-4 md:px-8 lg:px-10 lg:py-6">
          <SalonMark name={salon.displayName} logoUrl={salon.logoUrl} />
          <span className="headline text-lg text-heading">{salon.displayName}</span>
        </header>
      ) : null}
      <main className="flex flex-1 items-center justify-center px-4 py-14">
        <div className="w-full max-w-md animate-arrive">
          <div role="alert" className="flex flex-col gap-3 rounded-stack border border-edge-subtle bg-surface p-6 md:p-8">
            <span
              aria-hidden="true"
              className="mb-2 grid size-14 place-items-center rounded-full text-heading ring-1 ring-edge ring-inset"
            >
              <RotateCw className="size-6" strokeWidth={1.75} />
            </span>
            <p aria-hidden="true" className="eyebrow text-accent-ink">
              Page error
            </p>
            <h1 className="headline text-[2rem] leading-tight text-heading">Something went wrong</h1>
            <p className="text-[15px] leading-relaxed text-subtle">This page didn&apos;t load. Check your connection and try again.</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className={cn(pill.base, pill.accent, pill.lg, pill.wide, "mt-3", focusRing)}
            >
              <RotateCw className="size-4.5" aria-hidden />
              Reload
            </button>
            {salon?.branch?.phone ? (
              <a href={`tel:${salon.branch?.phone}`} className={cn(pill.base, pill.outline, pill.lg, pill.wide, focusRing)}>
                <Phone className="size-4.5" aria-hidden />
                Call <span className="tabular-nums">{formatPhone(salon.branch?.phone)}</span>
              </a>
            ) : null}
          </div>
        </div>
      </main>
    </div>
  );
}
